'use client';

import type { HTMLAttributes, PropsWithChildren } from 'react';
import React, { memo, useContext, useMemo } from 'react';
import styled from 'styled-components';
import type {
  Brands,
  ThemeDensity,
  Themes,
  ThemeSizing,
} from '@frameio/vapor-theme';
import { themeClassNames } from '@frameio/vapor-theme';
import { useThemeTransition } from './hooks';
import {
  ThemeProviderContext,
  type ThemeProviderContextValue,
} from './ThemeProviderContext';
import type {
  AccentNeutralColorsOptions,
  CustomAccentNeutralColors,
  CustomBrand,
  HexOrRgbColorString,
} from './types';
import {
  generateAccentNeutralColors,
  generateCustomAccentNeutralBrand,
  generateCustomBrand,
  getAccentNeutralThemeName,
  getCustomStyles,
} from './utils';

type GetThemeValueOptions = {
  themeContext: ThemeProviderContextValue;
  prop?:
    | Themes
    | Brands
    | CustomBrand
    | CustomAccentNeutralColors
    | ThemeDensity
    | ThemeSizing;
  contextKey:
    | 'themeName'
    | 'brandName'
    | 'customBrand'
    | 'accentNeutralColors'
    | 'density'
    | 'sizing';
};

type GetThemeValueReturnTypes<T> = T extends 'themeName'
  ? Themes | undefined
  : T extends 'brandName'
  ? Brands | undefined
  : T extends 'customBrand'
  ? CustomBrand | undefined
  : T extends 'accentNeutralColors'
  ? CustomAccentNeutralColors | undefined
  : T extends 'density'
  ? ThemeDensity | undefined
  : T extends 'sizing'
  ? ThemeSizing | undefined
  : never;

const getThemeValue = <T extends GetThemeValueOptions>({
  themeContext,
  prop,
  contextKey,
}: T): GetThemeValueReturnTypes<T['contextKey']> | undefined => {
  return (prop ?? themeContext?.[contextKey]) as
    | GetThemeValueReturnTypes<T['contextKey']>
    | undefined;
};

/**
 * Create a ThemeProvider to allow consumers to specify
 * which themes to use within react subtrees.
 */

type AccentNeutralBrandName = 'accent-neutral';

export type CustomAccentNeutralTheme = {
  name: AccentNeutralBrandName;
} & AccentNeutralColorsOptions;

export type ThemeOptions = Themes | CustomAccentNeutralTheme;

export type BrandOptions =
  | Brands
  | HexOrRgbColorString
  | AccentNeutralBrandName;

export type CustomAccentNeutralThemeAndBrand = {
  theme: CustomAccentNeutralTheme;
  brand: AccentNeutralBrandName | HexOrRgbColorString;
  density?: ThemeDensity;
  sizing?: ThemeSizing;
};

export type ThemeAndBrand = {
  theme?: Themes | null;
  brand?: BrandOptions;
  density?: ThemeDensity;
  sizing?: ThemeSizing;
};

// common props for ThemeProvider meant to be composed with CustomAccentNeutralThemeAndBrand or ThemeAndBrand
type ThemeProviderBaseProps = HTMLAttributes<'div'> & PropsWithChildren;

// allows for custom themes and brands to be passed in
export type ThemeProviderProps =
  | (CustomAccentNeutralThemeAndBrand & ThemeProviderBaseProps)
  | (ThemeAndBrand & ThemeProviderBaseProps);

const isCustomAccentNeutralThemeType = (
  themeProp: ThemeProviderProps['theme']
): themeProp is CustomAccentNeutralTheme => {
  if (!themeProp) {
    return false;
  }
  return typeof themeProp === 'object' && 'backgroundColor' in themeProp;
};

const isCustomBrandType = (
  brandProp: ThemeProviderProps['brand']
): brandProp is HexOrRgbColorString => {
  if (!brandProp) {
    return false;
  }
  return brandProp.includes('#') || brandProp.includes('rgb');
};

const getAccentNeutralBrandPrimaryColor = ({
  isCustomAccentNeutralTheme,
  themeProp,
  themeName,
}: {
  isCustomAccentNeutralTheme: boolean;
  themeProp: ThemeProviderProps['theme'];
  themeName?: Themes;
}): HexOrRgbColorString => {
  if (isCustomAccentNeutralTheme) {
    return (themeProp as CustomAccentNeutralTheme).backgroundColor;
  }
  return themeName && themeName === 'dark' ? '#FFFFFF' : '#000000';
};

/**
 * @description styled container for the theme provider
 */
const StyledThemeContainer = styled.div<
  Omit<ThemeProviderBaseProps, 'providerId'>
>`
  display: contents;
`;

/**
 * @description merge desired classnames from variadic arguments
 */
export const getClassnames = (...values: (string | undefined)[]) =>
  values.filter(Boolean).join(` `);

function ThemeProviderBase({
  theme: themeProp,
  brand: brandProp,
  density: densityProp,
  sizing: sizingProp,
  children,
  ...props
}: ThemeProviderProps) {
  // By default, inherit the theme from the parent theme provider.
  // This is useful for portal based components that need to inherit the theme
  // via context, since they can't rely on the cascading nature of css.
  const themeContext = useContext(ThemeProviderContext);

  const isCustomAccentNeutralTheme = isCustomAccentNeutralThemeType(themeProp);
  const isCustomBrand = isCustomBrandType(brandProp);
  const isCustomAccentNeutralBrand = brandProp === 'accent-neutral';
  const isRegularBrand = !isCustomBrand && !isCustomAccentNeutralBrand;

  const themeName = isCustomAccentNeutralTheme
    ? getAccentNeutralThemeName({
        backgroundColor: themeProp.backgroundColor,
      })
    : getThemeValue({
        themeContext,
        prop: themeProp ?? undefined,
        contextKey: 'themeName',
      });

  const customBrandFromProp = useMemo(() => {
    if (!isCustomBrand) {
      return undefined;
    }
    return generateCustomBrand({
      primaryColor: brandProp,
    });
  }, [brandProp, isCustomBrand]);

  const accentNeutralBrand = useMemo(() => {
    if (!isCustomAccentNeutralBrand) {
      return undefined;
    }
    return generateCustomAccentNeutralBrand({
      primaryColor: getAccentNeutralBrandPrimaryColor({
        isCustomAccentNeutralTheme,
        themeProp,
        themeName,
      }),
    });
  }, [
    isCustomAccentNeutralBrand,
    isCustomAccentNeutralTheme,
    themeProp,
    themeName,
  ]);

  const customBrand = isRegularBrand
    ? undefined
    : getThemeValue({
        themeContext,
        prop: customBrandFromProp,
        contextKey: 'customBrand',
      });

  const brandName =
    getThemeValue({
      themeContext,
      prop: isCustomAccentNeutralBrand || isCustomBrand ? undefined : brandProp,
      contextKey: 'brandName',
      // fallback to `fio` in the case that we're using a custom theme and the parent doesn't have a brand set so there's no brand in the context
    }) ?? 'fio';

  const accentNeutralColorsFromProp = useMemo(() => {
    if (!isCustomAccentNeutralTheme) {
      return undefined;
    }
    return generateAccentNeutralColors({
      backgroundColor: themeProp.backgroundColor,
      textColor: themeProp.textColor,
    });
  }, [isCustomAccentNeutralTheme, themeProp]);

  const accentNeutralColors = getThemeValue({
    themeContext,
    prop: accentNeutralColorsFromProp,
    contextKey: 'accentNeutralColors',
  });

  const density = getThemeValue({
    themeContext,
    prop: densityProp,
    contextKey: 'density',
  });

  const sizing = getThemeValue({
    themeContext,
    prop: sizingProp,
    contextKey: 'sizing',
  });

  const isOutermostThemeProvider =
    themeContext?.outermostThemeProviderValues === undefined;
  const context = useMemo(() => {
    return {
      ...themeContext,
      themeName,
      brandName,
      customBrand,
      accentNeutralBrand,
      accentNeutralColors,
      density,
      sizing,
      outermostThemeProviderValues: isOutermostThemeProvider
        ? {
            themeName,
            brandName,
            customBrand,
            accentNeutralBrand,
            accentNeutralColors,
            density,
            sizing,
          }
        : themeContext?.outermostThemeProviderValues,
    };
  }, [
    brandName,
    themeName,
    themeContext,
    customBrand,
    accentNeutralBrand,
    accentNeutralColors,
    density,
    sizing,
    isOutermostThemeProvider,
  ]);

  const themeTransitionGlobalStyles = useThemeTransition({ context });

  return (
    <ThemeProviderContext.Provider value={context}>
      {themeTransitionGlobalStyles}
      <StyledThemeContainer
        {...props}
        className={getClassnames(
          themeName ? themeClassNames[themeName] : undefined,
          themeClassNames[brandName],
          density
            ? themeClassNames[
                `density-${density}` as keyof typeof themeClassNames
              ]
            : undefined,
          sizing
            ? themeClassNames[
                `sizing-${sizing}` as keyof typeof themeClassNames
              ]
            : undefined
        )}
        data-theme={themeName}
        data-theme-brand={brandName}
        data-theme-custom-background={accentNeutralColors?.backgroundColor}
        data-theme-custom-brand={customBrand?.primary}
        data-theme-custom-text={accentNeutralColors?.textColor}
        style={{
          ...props.style,
          ...getCustomStyles({
            customBrand,
            accentNeutralBrand,
            accentNeutralColors,
          }),
        }}
        suppressHydrationWarning={true}
      >
        {children}
      </StyledThemeContainer>
    </ThemeProviderContext.Provider>
  );
}

export const ThemeProvider = memo(ThemeProviderBase);
ThemeProvider.displayName = `VaporThemeProvider`;
